import { DEFAULT_PIP_POSITION, type PipPosition } from "@/hooks/use-pip-drag"
import type { MessageMarker } from "@/types/chat"
import type { PlaceRef } from "@/types/entity"
import type { PlaceSummary } from "@/types/place"
import type { AgentStatusMetricPayload } from "@/types/socket"
import type {
  VoiceAudioProfile,
  VoiceChatToolCandidate,
  VoiceLiveEntry,
  VoiceLiveItem,
  VoiceToolCall,
  VoiceTranscriptPersistenceResult,
  VoiceTranscriptRole,
} from "@/types/voice-chat"
import {
  VOICE_AUDIO_SYSTEM_DEVICE_ID,
  type StopVoiceSessionArgs,
  type VoiceAudioDevice,
  type VoiceConnectionStatus,
  type VoiceSessionStateSnapshot,
} from "@/types/voice-chat-rtc"
import {
  createAction,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"

import { CAPTION_SYLLABLES_PER_SECOND_SEED } from "@/lib/voice-chat/caption-pacing"
import {
  revealedItemPlaces,
  UI_HIDDEN_VOICE_TOOL_NAMES,
  type VoiceTurnTransition,
} from "@/lib/voice-chat/voice-chat-utils"
import { toolCallProgressLabel } from "@/lib/voice-chat/voice-ui-utils"
import type { RootState } from "./redux-store"

/**
 * Serializable projection of live voice UI state, not its transport or
 * durability authority. Generation and audible playback are distinct; provider
 * item ids join realtime/persistence state while persisted message ids join
 * later annotations. `liveStream` deliberately interleaves transcript and tool
 * UI entries; runtime transport and side effects remain in middleware and RTC
 * collaborators.
 */
export interface VoiceChatState extends VoiceSessionStateSnapshot {
  selectedChatId: number | null
  connectionStatus: VoiceConnectionStatus
  openaiCallId: string | null
  muted: boolean
  // The assistant is held mid-answer: its speech is cut, and resuming
  // re-speaks the cut-off remainder verbatim (see VoiceChatRTC.setPaused).
  // Pausing also mutes the mic — a pause silences the whole session — but the
  // mute toggle stays live through it: unmuting reopens the uplink WITHOUT
  // resuming the held answer, and speaking then ends the pause through
  // `pauseAbandoned` (the assistant answers that instead of resuming).
  paused: boolean
  // Whether the current pause is what muted the mic. Ending the pause — by
  // resume or by speech — undoes only that mute, never one the customer chose
  // themselves; any manual flip of the toggle takes ownership and clears this.
  mutedByPause: boolean
  selectedSpeed: number
  // The server-owned audio settings this voice session started on. Held for the
  // session's lifetime so a start retry or reconnect re-acquires the microphone
  // with the same profile, whatever happened to the query cache meanwhile.
  audioProfile: VoiceAudioProfile | null
  liveUserTranscript: string
  liveStream: VoiceLiveEntry[]
  activeAssistantResponseId: string | null
  audibleAssistantResponseId: string | null
  activeAssistantItemId: string | null
  // Whether assistant audio is actually playing out of the WebRTC buffer.
  // Generation (`response.done`) finishes long before playback does, so
  // `assistant_speaking` must be released by audio buffer events, not the
  // response lifecycle.
  assistantAudioPlaying: boolean
  deletedProviderEventIds: string[]
  inputDevices: VoiceAudioDevice[]
  outputDevices: VoiceAudioDevice[]
  selectedInputDeviceId: string
  selectedOutputDeviceId: string
  speakerSelectionSupported: boolean
  errorMessage: string | null
  captionSyllablesPerSecond: number
  // Speech gate over the assistant's playback level: true/false while the
  // engine meters the inbound audio, null when the browser reports no level
  // (the caption pacer then runs ungated). Updated on transitions only.
  assistantSpeaking: boolean | null
  // The engine's per-audio-window regime latch (single owner): whether the
  // current audible window's pacing runs gated. The scheduler follows this
  // instead of latching its own copy.
  assistantWindowGated: boolean
  // Non-authoritative presentation signal from the inbound WebRTC level sampler:
  // sustained near-zero playback within the current audio window.
  assistantAudioStalled: boolean
  // Full-screen voice-mode takeover launched from normal chat; independent of
  // the connection lifecycle (opening the overlay does not start a session).
  voiceModeOpen: boolean
  voiceModeChatId: number | null
  // The place whose page the call started from (PlaceQuestionForm), sent with
  // every session request so a reconnect keeps it in the instructions.
  voiceModeEntityRef: PlaceRef | null
  // The call is running, but put down: the overlay is gone and the mini-player
  // carries it. A sheet covering the overlay ends up showing the same pair of
  // surfaces, but that one is derived from the sheet slices
  // (selectVoiceOverlayCoveredBySheet) and lasts exactly as long as the sheet.
  // This is the user asking for it, so nothing can infer it — only they, and
  // the end of the session, clear it.
  voiceModeMinimized: boolean
  // Which corner the mini-player was last dragged to. It lives here rather
  // than in the player because the player is exactly what does not survive the
  // question: expanding unmounts it, and a corner that died with it sent the
  // user's card back to the default every time they looked at the call. Its
  // lifetime is the call's, which is what the reset points below encode — a
  // NEW call always starts at DEFAULT_PIP_POSITION, so the first collapse of a
  // session is predictable, while a collapse/expand/collapse within one call
  // puts the card back where the user left it.
  //
  // Not persisted, and that is the distinction this replaced: a corner chosen
  // while reading one place card must not survive to the next session.
  voicePipPosition: PipPosition
  // Whether this call has heard a committed customer turn — a hang-up lands
  // on the chat's transcript only once there is one (hangUpVoiceMode). Latched
  // rather than derived from liveStream, which reconnects and reconciliation
  // clear mid-call — a wipe must not turn a real conversation back into a
  // silent one.
  voiceModeUserSpoke: boolean
}

// Hard DOM ceiling on the caption's CANDIDATE window (see
// `selectRecentCaptionItemIds`). Which candidates actually stay on screen is
// decided by geometry in the caption component — a turn retires once it has
// fully scrolled out of the visible band — so this only bounds how much the
// caption can ever mount at once (e.g. reopening voice mode onto a long
// session backlog).
const MAX_CAPTION_TURNS = 8

const initialState: VoiceChatState = {
  selectedChatId: null,
  connectionStatus: "idle",
  openaiCallId: null,
  muted: false,
  paused: false,
  mutedByPause: false,
  selectedSpeed: 1.0,
  audioProfile: null,
  liveUserTranscript: "",
  liveStream: [],
  activeAssistantResponseId: null,
  audibleAssistantResponseId: null,
  activeAssistantItemId: null,
  assistantAudioPlaying: false,
  deletedProviderEventIds: [],
  inputDevices: [],
  outputDevices: [],
  selectedInputDeviceId: VOICE_AUDIO_SYSTEM_DEVICE_ID,
  selectedOutputDeviceId: VOICE_AUDIO_SYSTEM_DEVICE_ID,
  speakerSelectionSupported: false,
  errorMessage: null,
  captionSyllablesPerSecond: CAPTION_SYLLABLES_PER_SECOND_SEED,
  assistantSpeaking: null,
  assistantWindowGated: false,
  assistantAudioStalled: false,
  voiceModeOpen: false,
  voiceModeChatId: null,
  voiceModeEntityRef: null,
  voiceModeMinimized: false,
  voicePipPosition: DEFAULT_PIP_POSITION,
  voiceModeUserSpoke: false,
}

function findLiveItem(state: VoiceChatState, itemId: string) {
  return state.liveStream.find(
    (entry): entry is Extract<VoiceLiveEntry, { kind: "item" }> =>
      entry.kind === "item" && entry.item.itemId === itemId
  )?.item
}

function collapseMergedCustomerItem(
  state: VoiceChatState,
  providerEventId: string,
  messageId: number
) {
  const mergedItemIndex = state.liveStream.findIndex(
    (entry) =>
      entry.kind === "item" &&
      entry.item.role === "user" &&
      entry.item.itemId !== providerEventId &&
      entry.item.messageId === messageId
  )
  if (mergedItemIndex >= 0) state.liveStream.splice(mergedItemIndex, 1)
  return findLiveItem(state, providerEventId)
}

function ensureLiveItem(
  state: VoiceChatState,
  itemId: string,
  role: VoiceTranscriptRole
) {
  const existing = findLiveItem(state, itemId)
  if (existing) return existing

  const item: VoiceLiveItem = {
    itemId,
    role,
    text: "",
    revealedLength: 0,
    status: "in_progress",
    interrupted: false,
  }
  state.liveStream.push({ kind: "item", item })
  return item
}

function findToolCall(state: VoiceChatState, callId: string) {
  return state.liveStream.find(
    (entry): entry is Extract<VoiceLiveEntry, { kind: "tool" }> =>
      entry.kind === "tool" && entry.toolCall.callId === callId
  )?.toolCall
}

function moveToolCallToStreamTail(state: VoiceChatState, callId: string) {
  const toolEntryIndex = state.liveStream.findIndex(
    (entry) => entry.kind === "tool" && entry.toolCall.callId === callId
  )
  if (toolEntryIndex === -1 || toolEntryIndex === state.liveStream.length - 1) {
    return
  }

  state.liveStream.push(...state.liveStream.splice(toolEntryIndex, 1))
}

// Back-to-back responses (a tool-result narration queued behind its filler)
// play gaplessly: the buffer never drains in between, so the successor gets
// no `output_audio_buffer.started` of its own and its caption would never
// pace. Once the audible caption is fully revealed while audio still flows,
// the remaining unrevealed text is that successor — hand the audible id over.
// The pacer finishing the predecessor's reveal is what triggers this (it
// paces to the predecessor's audio, so the handoff lands near the true item
// boundary). Earlier turns can't qualify: `responseStarted`/`audioStopped`
// snap them fully revealed once their audio has played out.
function advanceAudibleAssistantResponse(state: VoiceChatState) {
  if (!state.audibleAssistantResponseId || !state.assistantAudioPlaying) {
    return
  }
  let successorResponseId: string | undefined
  for (const entry of state.liveStream) {
    if (entry.kind !== "item" || entry.item.role !== "assistant") continue
    if (entry.item.revealedLength >= entry.item.text.length) continue
    // The audible response is still revealing; no handoff.
    if (entry.item.responseId === state.audibleAssistantResponseId) return
    successorResponseId ??= entry.item.responseId
  }
  if (successorResponseId) {
    state.audibleAssistantResponseId = successorResponseId
  }
}

function resetAssistantInterruptionState(state: VoiceChatState) {
  state.activeAssistantResponseId = null
  state.audibleAssistantResponseId = null
  state.activeAssistantItemId = null
  state.assistantAudioPlaying = false
  state.assistantSpeaking = null
  state.assistantWindowGated = false
  state.assistantAudioStalled = false
  state.deletedProviderEventIds = []
}

function selectedDeviceStillAvailable(
  selectedDeviceId: string,
  devices: VoiceAudioDevice[]
) {
  return (
    selectedDeviceId === VOICE_AUDIO_SYSTEM_DEVICE_ID ||
    devices.some((device) => device.deviceId === selectedDeviceId)
  )
}

// Why voice mode ended. "left_chat" is leaving the page the call lives on: the
// middleware tears the session down once the viewed chat is no longer the one
// the call started on, and on any pathname-changing navigation (its
// setPreviousPage listener) — the case chat actions never cover for calls on
// non-chat pages (trips, home).
// "chat_started" is the hang-up-first rule: the user sent a message instead of
// speaking — which creates a chat from the home route, but more often just
// posts into the chat already on screen.
// Required, not defaulted: a call site that ends a call programmatically must
// say so, or it would be indistinguishable from the user hanging up — the one
// reason a rollout decision reads as a healthy session. The reducer itself
// doesn't care; this exists for the VoiceSessionEnd event.
export type VoiceModeCloseReason = "user_ended" | "left_chat" | "chat_started"

// Where the open flow died. "audio_profile", "mic" and "chat_create" happen
// before voice mode opens at all; "connection" is the session-start pipeline
// failing afterwards.
export type VoiceSessionFailure =
  | "audio_profile"
  | "mic"
  | "chat_create"
  | "connection"

const voiceChatSlice = createSlice({
  name: "voiceChat",
  initialState,
  reducers: {
    voiceModeOpened(
      state,
      action: PayloadAction<{
        chatId: number
        mediaStream: Promise<MediaStream>
        audioProfile: VoiceAudioProfile
        entityRef?: PlaceRef | null
        // Open straight into the mini-player, never showing the full overlay.
        minimized?: boolean
      }>
    ) {
      state.voiceModeOpen = true
      state.voiceModeChatId = action.payload.chatId
      state.voiceModeEntityRef = action.payload.entityRef ?? null
      // A call arrives on the full surface unless it asks for the mini-player,
      // and in the default corner, whatever the last one was left as.
      state.voiceModeMinimized = action.payload.minimized ?? false
      state.voicePipPosition = DEFAULT_PIP_POSITION
      state.voiceModeUserSpoke = false
      state.audioProfile = action.payload.audioProfile
    },
    voiceModeClosed(
      state,
      _action: PayloadAction<{ reason: VoiceModeCloseReason }>
    ) {
      state.voiceModeOpen = false
      state.voiceModeChatId = null
      state.voiceModeEntityRef = null
      state.voiceModeMinimized = false
      state.voicePipPosition = DEFAULT_PIP_POSITION
      state.audioProfile = null
    },
    // Put the call down without ending it. The session runs on underneath; only
    // the surface it is presented on changes.
    voiceModeCollapsed(state) {
      state.voiceModeMinimized = true
    },
    voiceModeExpanded(state) {
      state.voiceModeMinimized = false
    },
    // Survives the expand that unmounts the player — see voicePipPosition.
    voicePipMoved(state, action: PayloadAction<PipPosition>) {
      state.voicePipPosition = action.payload
    },
    connectingStarted(state) {
      state.connectionStatus = "connecting"
      state.liveStream = []
      resetAssistantInterruptionState(state)
      state.errorMessage = null
    },
    reconnectingStarted(state) {
      state.connectionStatus = "connecting"
      resetAssistantInterruptionState(state)
      state.errorMessage = null
    },
    transportReconnecting(state) {
      if (
        state.connectionStatus === "listening" ||
        state.connectionStatus === "assistant_speaking"
      ) {
        state.connectionStatus = "reconnecting"
      }
    },
    transportRecovered(state) {
      if (state.connectionStatus === "reconnecting") {
        state.connectionStatus = "listening"
      }
    },
    connected(
      state,
      action: PayloadAction<{
        chatId: number
        openaiCallId: string
      }>
    ) {
      state.selectedChatId = action.payload.chatId
      state.connectionStatus = "listening"
      state.openaiCallId = action.payload.openaiCallId
      state.muted = false
      state.paused = false
      state.mutedByPause = false
      state.liveStream = []
      state.errorMessage = null
      resetAssistantInterruptionState(state)
    },
    reconnected(
      state,
      action: PayloadAction<{ chatId: number; openaiCallId: string }>
    ) {
      state.selectedChatId = action.payload.chatId
      state.connectionStatus = "listening"
      state.openaiCallId = action.payload.openaiCallId
      // The customer's mute carries across; a pause's died with the old call.
      state.muted = state.muted && !state.mutedByPause
      state.paused = false
      state.mutedByPause = false
      state.errorMessage = null
      resetAssistantInterruptionState(state)
    },
    disconnected(state) {
      state.connectionStatus = "idle"
      state.openaiCallId = null
      state.muted = false
      state.paused = false
      state.mutedByPause = false
      state.liveUserTranscript = ""
      resetAssistantInterruptionState(state)
      state.errorMessage = null
    },
    sessionReconciled(state) {
      state.liveStream = []
      resetAssistantInterruptionState(state)
    },
    failed(state, action: PayloadAction<string>) {
      state.connectionStatus = "error"
      state.muted = false
      state.paused = false
      state.mutedByPause = false
      state.errorMessage = action.payload
    },
    mutedToggled(state) {
      state.muted = !state.muted
      // A manual flip takes ownership of the mic: whatever ends the pause
      // afterwards must not undo a choice the customer made themselves.
      state.mutedByPause = false
    },
    pausedToggled(state) {
      state.paused = !state.paused
      if (state.paused) {
        // Pausing silences the whole session, mic included. Remember whether
        // the pause did the muting so ending it restores only what it took —
        // a mic the customer muted before pausing stays muted after.
        state.mutedByPause = !state.muted
        state.muted = true
      } else if (state.mutedByPause) {
        state.muted = false
        state.mutedByPause = false
      }
    },
    captionRateMeasured(state, action: PayloadAction<number>) {
      state.captionSyllablesPerSecond = action.payload
    },
    assistantSpeakingChanged(
      state,
      action: PayloadAction<{
        speaking: boolean | null
        windowGated: boolean
        assistantAudioStalled: boolean
      }>
    ) {
      state.assistantSpeaking = action.payload.speaking
      state.assistantWindowGated = action.payload.windowGated
      state.assistantAudioStalled = action.payload.assistantAudioStalled
    },
    selectedSpeedChanged(state, action: PayloadAction<number>) {
      state.selectedSpeed = action.payload
    },
    audioDevicesChanged(
      state,
      action: PayloadAction<{
        inputDevices: VoiceAudioDevice[]
        outputDevices: VoiceAudioDevice[]
        speakerSelectionSupported: boolean
      }>
    ) {
      state.inputDevices = action.payload.inputDevices
      state.outputDevices = action.payload.outputDevices
      state.speakerSelectionSupported = action.payload.speakerSelectionSupported

      if (
        !selectedDeviceStillAvailable(
          state.selectedInputDeviceId,
          state.inputDevices
        )
      ) {
        state.selectedInputDeviceId = VOICE_AUDIO_SYSTEM_DEVICE_ID
      }

      if (
        !state.speakerSelectionSupported ||
        !selectedDeviceStillAvailable(
          state.selectedOutputDeviceId,
          state.outputDevices
        )
      ) {
        state.selectedOutputDeviceId = VOICE_AUDIO_SYSTEM_DEVICE_ID
      }
    },
    selectedInputDeviceChanged(state, action: PayloadAction<string>) {
      state.selectedInputDeviceId = action.payload
    },
    selectedOutputDeviceChanged(state, action: PayloadAction<string>) {
      state.selectedOutputDeviceId = action.payload
    },
    // VoiceChatRTC owns provider ordering and dispatches these transitions
    // synchronously. This projection distinguishes response generation from
    // audible buffer lifecycle; transcript completion never makes Redux durable.
    turnTransitionApplied(state, action: PayloadAction<VoiceTurnTransition>) {
      const transition = action.payload

      if (transition.kind === "turnStarted") {
        ensureLiveItem(state, transition.id, transition.role)
        return
      }

      if (transition.kind === "userTranscriptDelta") {
        state.liveUserTranscript += transition.delta
        if (transition.id) {
          const liveItem = ensureLiveItem(state, transition.id, "user")
          liveItem.text += transition.delta
          liveItem.revealedLength = liveItem.text.length
        }
        return
      }

      if (transition.kind === "assistantTranscriptDelta") {
        state.activeAssistantResponseId = transition.responseId
        state.activeAssistantItemId = transition.id
        const liveItem = ensureLiveItem(state, transition.id, "assistant")
        liveItem.responseId = transition.responseId
        if (!liveItem.interrupted) {
          liveItem.text += transition.delta
        }
        advanceAudibleAssistantResponse(state)
        return
      }

      if (transition.kind === "userSpeechStarted") {
        if (state.connectionStatus !== "reconnecting") {
          state.connectionStatus = "listening"
        }
        state.liveUserTranscript = ""
        return
      }

      if (transition.kind === "responseStarted") {
        if (state.connectionStatus !== "reconnecting") {
          state.connectionStatus = "assistant_speaking"
        }
        state.activeAssistantResponseId = transition.responseId
        state.activeAssistantItemId = null
        // Prior assistant turns snap fully revealed so a lagging finished
        // turn can't keep draining the pacer's shared reveal budget — but
        // only turns whose audio has actually played out. Generation outruns
        // playback: in a gapless tool chain this `response.created` arrives
        // while the AUDIBLE response still has audio in the buffer, and
        // snapping it dumped a 536-char narration ~25s before its audio
        // finished, handed the audible id to the successor, and wedged the
        // caption dark for 28s behind the phantom carry
        // (voice-trace-7369273). Audio plays serially, so entries strictly
        // BEFORE the audible response's first item are the played-out ones;
        // the audible response and everything after it are still pacing.
        let snapBefore = state.liveStream.length
        if (state.assistantAudioPlaying && state.audibleAssistantResponseId) {
          const audibleIndex = state.liveStream.findIndex(
            (entry) =>
              entry.kind === "item" &&
              entry.item.responseId === state.audibleAssistantResponseId
          )
          // The audible response's items missing from the stream is a
          // bookkeeping edge: snap nothing — briefly draining budget on an
          // old turn beats snapping one that may still be playing.
          snapBefore = audibleIndex === -1 ? 0 : audibleIndex
        }
        for (const entry of state.liveStream.slice(0, snapBefore)) {
          // Interrupted turns are exempt: a normal interruption already
          // trimmed text to the reveal (no-op), while a PAUSED turn
          // deliberately keeps its unspoken dimmed tail (turnPaused) — the
          // resume's own response.created must not snap it fully revealed
          // before its audio has said a word.
          if (
            entry.kind === "item" &&
            entry.item.role === "assistant" &&
            !entry.item.interrupted
          ) {
            entry.item.revealedLength = entry.item.text.length
          }
        }
        return
      }

      if (transition.kind === "assistantItemAdded") {
        if (transition.itemType !== "message" || !transition.role) return

        const liveItem = ensureLiveItem(state, transition.id, transition.role)
        if (transition.role === "assistant") {
          liveItem.responseId = transition.responseId
          state.activeAssistantResponseId = transition.responseId
          state.activeAssistantItemId = transition.id
        }
        return
      }

      if (transition.kind === "turnInterrupted") {
        const liveItem = findLiveItem(state, transition.id)
        if (liveItem) {
          if (liveItem.role === "assistant") {
            liveItem.text = liveItem.text.slice(0, liveItem.revealedLength)
            liveItem.revealedLength = liveItem.text.length
          }
          liveItem.interrupted = true
          liveItem.status = "completed"
        }
        if (state.activeAssistantItemId === transition.id) {
          state.activeAssistantItemId = null
        }
        return
      }

      // The pause cut: freeze the reveal at the word boundary the resume will
      // continue from — backing up past a mid-word fragment — while the text
      // keeps (and absorbs, via `text`) the unspoken remainder, so the dimmed
      // tail stays on screen through the pause. `interrupted` closes the item
      // against deltas still in flight from the cancelled response; the resume
      // reopens it (turnResumed) and reveals over this same text.
      if (transition.kind === "turnPaused") {
        const liveItem = findLiveItem(state, transition.id)
        if (liveItem) {
          liveItem.text = transition.text
          liveItem.revealedLength = Math.min(
            liveItem.revealedLength,
            transition.heardLength
          )
          liveItem.interrupted = true
          liveItem.status = "completed"
        }
        if (state.activeAssistantItemId === transition.id) {
          state.activeAssistantItemId = null
        }
        return
      }

      // A pause-resume continuation reopens the paused turn so its deltas
      // append here rather than opening a new caption row. The heard prefix
      // is recorded so the continuation window's pacing excludes it — its
      // audio never speaks those chars, and counting them as this window's
      // revealed speech ran the pacer's deficit negative by the whole prefix,
      // freezing the reveal for as long as the pre-pause audio had played.
      if (transition.kind === "turnResumed") {
        const liveItem = findLiveItem(state, transition.id)
        if (liveItem) {
          liveItem.interrupted = false
          liveItem.status = "in_progress"
          liveItem.pausePrefixLength = transition.heardLength
        }
        return
      }

      // Speaking while paused supersedes the held remainder, so the pause ends
      // here instead of at the resume button. The dimmed tail the pause kept
      // on screen goes with it: it was only ever there because the resume was
      // going to speak it, and the interrupted write already persisted this
      // same heard prefix as the turn's body.
      if (transition.kind === "pauseAbandoned") {
        state.paused = false
        // A speech_started already in flight when the pause landed abandons it
        // before the customer ever touches the toggle: the pause's auto-mute
        // goes with the pause, or they'd be left silently muted mid-sentence
        // in a conversation that just continued.
        if (state.mutedByPause) {
          state.muted = false
          state.mutedByPause = false
        }
        const liveItem =
          transition.id === null ? null : findLiveItem(state, transition.id)
        if (liveItem) {
          liveItem.text = liveItem.text.slice(0, transition.heardLength)
          liveItem.revealedLength = Math.min(
            liveItem.revealedLength,
            transition.heardLength
          )
        }
        return
      }

      if (transition.kind === "responseFinished") {
        // A response-scoped terminal event must not release a newer response's
        // generation state. Events without a response id retain the legacy
        // fallback because there is no provider identity available to compare.
        if (
          !transition.responseId ||
          state.activeAssistantResponseId === transition.responseId
        ) {
          state.activeAssistantResponseId = null
          state.activeAssistantItemId = null
        }
        // `response.done` means generation finished; the audio buffer keeps
        // playing afterwards, and `audioStopped` releases the status then.
        // Flipping here only covers responses that never produced audio.
        if (
          !state.assistantAudioPlaying &&
          state.connectionStatus === "assistant_speaking"
        ) {
          state.connectionStatus = "listening"
        }
        return
      }

      if (transition.kind === "sessionError") {
        state.connectionStatus = "error"
        // Provider internals never reach the surface: null renders the neutral
        // status copy, and the raw message already lives in the trace.
        state.errorMessage = null
        // The session is dead — release the audible window too, or the pacer
        // keeps ticking against the stale response id until the user stops.
        resetAssistantInterruptionState(state)
        return
      }

      if (transition.kind === "turnCompleted") {
        if (state.deletedProviderEventIds.includes(transition.id)) return

        const liveItem = ensureLiveItem(state, transition.id, transition.role)
        liveItem.text = transition.text
        liveItem.status = "completed"

        if (transition.role === "user") {
          liveItem.revealedLength = transition.text.length
          state.liveUserTranscript = ""
          state.voiceModeUserSpoke = true
        } else {
          state.activeAssistantResponseId = null
          state.activeAssistantItemId = null
        }
        return
      }

      if (transition.kind === "audioStarted") {
        state.assistantAudioPlaying = true
        if (state.connectionStatus !== "reconnecting") {
          state.connectionStatus = "assistant_speaking"
        }
        state.audibleAssistantResponseId = transition.responseId
        return
      }

      if (transition.kind === "audioStopped") {
        state.assistantAudioPlaying = false
        state.assistantAudioStalled = false
        if (state.connectionStatus === "assistant_speaking") {
          state.connectionStatus = "listening"
        }
        // The buffer drained: the stopped response and — audio is serial —
        // everything before it have fully played, so all of them snap
        // revealed. In a gapless chain the stop event carries the LAST
        // response's id while a lagging predecessor can still be mid-reveal;
        // snapping by id alone left that predecessor's tail unrevealed until
        // the next turn. Entries after the stopped response (a newer,
        // already-started buffer) stay untouched.
        let snapThrough = -1
        state.liveStream.forEach((entry, index) => {
          if (
            entry.kind === "item" &&
            entry.item.responseId === transition.responseId
          ) {
            snapThrough = index
          }
        })
        for (const entry of state.liveStream.slice(0, snapThrough + 1)) {
          if (entry.kind === "item" && entry.item.role === "assistant") {
            entry.item.revealedLength = entry.item.text.length
          }
        }
        const audibleId = state.audibleAssistantResponseId
        if (audibleId === transition.responseId) {
          state.audibleAssistantResponseId = null
        } else if (audibleId) {
          // A lagging predecessor can still hold the audible id when the
          // chain drains under the successor's id. It sits at-or-before the
          // stopped response in the stream, so its audio has provably played
          // out — release it. (An audible response AFTER the stopped one —
          // an out-of-order stale stop — keeps the id and keeps pacing.)
          const audibleDrained = state.liveStream.some(
            (entry, index) =>
              index <= snapThrough &&
              entry.kind === "item" &&
              entry.item.responseId === audibleId
          )
          if (audibleDrained) {
            state.audibleAssistantResponseId = null
          }
        }
        return
      }

      // Buffer cleared (barge-in or manual stop) rather than played out;
      // `turnInterrupted` trims the caption, so only playback state releases.
      if (transition.kind === "audioCleared") {
        state.assistantAudioPlaying = false
        state.assistantAudioStalled = false
        if (state.connectionStatus === "assistant_speaking") {
          state.connectionStatus = "listening"
        }
        if (state.audibleAssistantResponseId === transition.responseId) {
          state.audibleAssistantResponseId = null
        }
      }
    },
    assistantTranscriptPaced(state, action: PayloadAction<{ length: number }>) {
      // A paused session's reveal is frozen at the pause cut. The pacer keeps
      // ticking until the server confirms the clear (audible id released), and
      // those racing ticks crept the reveal past the cut the resume re-speaks
      // from.
      if (state.paused) return
      if (!state.audibleAssistantResponseId) return

      let remainingLength = action.payload.length
      const revealableItems = state.liveStream.flatMap((entry) =>
        entry.kind === "item" &&
        entry.item.role === "assistant" &&
        entry.item.responseId === state.audibleAssistantResponseId &&
        entry.item.revealedLength < entry.item.text.length
          ? [entry.item]
          : []
      )

      for (const item of revealableItems) {
        const itemRemainingLength = item.text.length - item.revealedLength
        const revealedLength = Math.min(itemRemainingLength, remainingLength)
        item.revealedLength += revealedLength
        remainingLength -= revealedLength

        if (remainingLength === 0) break
      }
      // This tick may have finished the audible caption while a gapless
      // successor is queued — hand over so the pacer stays active.
      advanceAudibleAssistantResponse(state)
    },
    turnPersisting(state, action: PayloadAction<{ providerEventId: string }>) {
      const liveItem = findLiveItem(state, action.payload.providerEventId)
      if (liveItem) {
        liveItem.persistenceStatus = "persisting"
        liveItem.persistenceError = null
      }
    },
    turnPersisted(
      state,
      action: PayloadAction<
        {
          providerEventId: string
          interrupted?: boolean
        } & VoiceTranscriptPersistenceResult
      >
    ) {
      if (action.payload.deleted === true) {
        state.deletedProviderEventIds.push(action.payload.providerEventId)
        state.liveStream = state.liveStream.filter(
          (entry) =>
            entry.kind !== "item" ||
            entry.item.itemId !== action.payload.providerEventId
        )
        return
      }

      const liveItem = collapseMergedCustomerItem(
        state,
        action.payload.providerEventId,
        action.payload.messageId
      )
      if (liveItem) {
        liveItem.persistenceStatus = "persisted"
        liveItem.messageId = action.payload.messageId
        // The interrupted guard: a pause-resume can reopen the turn
        // (turnResumed) before the pause's interrupted write resolves, and
        // that result's heard-prefix body is then older than the live merged
        // text — it must not clobber it or re-close the turn.
        if (action.payload.interrupted === true && liveItem.interrupted) {
          if (liveItem.revealedLength >= liveItem.text.length) {
            liveItem.text = action.payload.body
            liveItem.revealedLength = action.payload.body.length
          } else {
            // A PAUSED turn keeps its dimmed unspoken tail (revealed < text,
            // which a normal fully-trimmed interruption never leaves behind):
            // the write's body is just the heard prefix, so aligning text to
            // it would erase what the pause deliberately kept on screen.
            liveItem.revealedLength = Math.min(
              liveItem.revealedLength,
              action.payload.body.length
            )
          }
          liveItem.interrupted = true
        } else if (
          action.payload.interrupted !== true &&
          !(liveItem.role === "assistant" && liveItem.interrupted)
        ) {
          liveItem.text = action.payload.body
          liveItem.revealedLength =
            liveItem.role === "assistant"
              ? Math.min(liveItem.revealedLength, action.payload.body.length)
              : action.payload.body.length
        }
        if (
          action.payload.interrupted === true ||
          liveItem.role === "user" ||
          action.payload.markers.length > 0 ||
          action.payload.places.length > 0
        ) {
          liveItem.markers = action.payload.markers
          liveItem.places = action.payload.places
        }
        liveItem.flightSummaryMessageId = action.payload.flightSummaryMessageId
        liveItem.persistenceError = null
      }
    },
    turnAnnotationApplied(
      state,
      action: PayloadAction<{
        providerEventId: string
        markers: MessageMarker[]
        places: PlaceSummary[]
      }>
    ) {
      const liveItem = findLiveItem(state, action.payload.providerEventId)
      if (!liveItem) return

      liveItem.markers = action.payload.markers
      liveItem.places = action.payload.places
    },
    turnPersistFailed(
      state,
      action: PayloadAction<{ providerEventId: string; message: string }>
    ) {
      const liveItem = findLiveItem(state, action.payload.providerEventId)
      if (liveItem) {
        liveItem.persistenceStatus = "persist_failed"
        liveItem.persistenceError = action.payload.message
      }
    },
    toolCallStarted(
      state,
      action: PayloadAction<{
        callId: string
        toolName: string
        // The session usually supplies localized copy. Missing test/recovery
        // labels use the helper's known-skill or generic fallback.
        progressLabel?: string
        query: string
        locationHint?: string
      }>
    ) {
      const progressLabel =
        action.payload.progressLabel ??
        toolCallProgressLabel(action.payload.toolName)
      const existing = findToolCall(state, action.payload.callId)
      if (existing) {
        existing.toolName = action.payload.toolName
        existing.progressLabel = progressLabel
        existing.query = action.payload.query
        existing.locationHint = action.payload.locationHint ?? null
        existing.status = "pending"
        existing.statusMessage = null
        existing.estimatedCompletionPercent = null
        existing.complicationMessage = null
        existing.metricPayload = null
        existing.candidates = []
        existing.hotelQuoteRefs = []
        existing.errorMessage = null
        return
      }

      state.liveStream.push({
        kind: "tool",
        toolCall: {
          callId: action.payload.callId,
          toolName: action.payload.toolName,
          progressLabel,
          query: action.payload.query,
          locationHint: action.payload.locationHint ?? null,
          status: "pending",
          statusMessage: null,
          estimatedCompletionPercent: null,
          complicationMessage: null,
          metricPayload: null,
          candidates: [],
          hotelQuoteRefs: [],
          errorMessage: null,
        },
      })
    },
    toolCallProgressed(
      state,
      action: PayloadAction<{
        callId: string
        statusMessage?: string
        estimatedCompletionPercent?: number
        complicationMessage?: string
        metricPayload?: AgentStatusMetricPayload
      }>
    ) {
      const toolCall = findToolCall(state, action.payload.callId)
      if (!toolCall || toolCall.status !== "pending") return

      if (action.payload.statusMessage !== undefined) {
        toolCall.statusMessage = action.payload.statusMessage
        toolCall.estimatedCompletionPercent =
          action.payload.estimatedCompletionPercent ?? null
        toolCall.complicationMessage =
          action.payload.complicationMessage ?? null
      } else if (action.payload.estimatedCompletionPercent !== undefined) {
        toolCall.estimatedCompletionPercent =
          action.payload.estimatedCompletionPercent
      }
      if (action.payload.metricPayload !== undefined) {
        toolCall.metricPayload = action.payload.metricPayload
      }
    },
    toolCallSucceeded(
      state,
      action: PayloadAction<{
        callId: string
        candidates: VoiceChatToolCandidate[]
        hotelQuoteRefs: string[]
      }>
    ) {
      const toolCall = findToolCall(state, action.payload.callId)
      if (!toolCall) {
        state.liveStream.push({
          kind: "tool",
          toolCall: {
            callId: action.payload.callId,
            toolName: "place_lookup",
            progressLabel: toolCallProgressLabel("place_lookup"),
            query: "Place lookup",
            locationHint: null,
            status: "done",
            statusMessage: null,
            estimatedCompletionPercent: null,
            complicationMessage: null,
            metricPayload: null,
            candidates: action.payload.candidates,
            hotelQuoteRefs: action.payload.hotelQuoteRefs,
            errorMessage: null,
          },
        })
        return
      }

      toolCall.status = "done"
      toolCall.statusMessage = null
      toolCall.estimatedCompletionPercent = null
      toolCall.complicationMessage = null
      toolCall.metricPayload = null
      toolCall.candidates = action.payload.candidates
      toolCall.hotelQuoteRefs = action.payload.hotelQuoteRefs
      toolCall.errorMessage = null
      moveToolCallToStreamTail(state, action.payload.callId)
    },
    toolCallFailed(
      state,
      action: PayloadAction<{
        callId: string
        message: string
        toolName: string
        query?: string
        locationHint?: string
      }>
    ) {
      const toolCall = findToolCall(state, action.payload.callId)
      if (!toolCall) {
        state.liveStream.push({
          kind: "tool",
          toolCall: {
            callId: action.payload.callId,
            toolName: action.payload.toolName,
            progressLabel: toolCallProgressLabel(action.payload.toolName),
            query: action.payload.query ?? action.payload.toolName,
            locationHint: action.payload.locationHint ?? null,
            status: "failed",
            statusMessage: null,
            estimatedCompletionPercent: null,
            complicationMessage: null,
            metricPayload: null,
            candidates: [],
            hotelQuoteRefs: [],
            errorMessage: action.payload.message,
          },
        })
        return
      }

      toolCall.status = "failed"
      toolCall.statusMessage = null
      toolCall.estimatedCompletionPercent = null
      toolCall.complicationMessage = null
      toolCall.metricPayload = null
      toolCall.candidates = []
      toolCall.hotelQuoteRefs = []
      toolCall.errorMessage = action.payload.message
      moveToolCallToStreamTail(state, action.payload.callId)
    },
  },
})

// The root these selectors read: the slice alone. Voice mode passes the app
// store's RootState, which satisfies it; the setup voice interview passes a
// root built around its adapter's private copy of this state, so the pause
// and pacing rules below are the same rules on both sessions.
export type VoiceRootState = Pick<RootState, "voiceChatSlice">

export const selectVoiceChatState = (state: VoiceRootState) =>
  state.voiceChatSlice

export const selectVoiceConnectionStatus = (state: VoiceRootState) =>
  state.voiceChatSlice.connectionStatus

export const selectCaptionSyllablesPerSecond = (state: VoiceRootState) =>
  state.voiceChatSlice.captionSyllablesPerSecond

export const selectAudibleAssistantResponseId = (state: VoiceRootState) =>
  state.voiceChatSlice.audibleAssistantResponseId

export const selectAssistantSpeaking = (state: VoiceRootState) =>
  state.voiceChatSlice.assistantSpeaking

// True while assistant audio is actually in the playback buffer (bounded by
// `output_audio_buffer.started/stopped/cleared`), unlike `assistant_speaking`
// status which flips on at `response.created` — before anything is audible.
export const selectAssistantAudioPlaying = (state: VoiceRootState) =>
  state.voiceChatSlice.assistantAudioPlaying

// One definition of "still connecting" for every surface that shows it. It
// ends at `connected`, which the middleware dispatches once the data channel is
// open and the start sound plays — the first thing the customer hears, and the
// moment anything they say will land. `error` wins outright — the orb turns
// red, and a connect ring pinging around it would read as a session still
// trying.
export const isVoiceConnecting = (status: VoiceConnectionStatus) =>
  status === "idle" || status === "connecting" || status === "reconnecting"

export const selectVoiceConnecting = (state: VoiceRootState) =>
  isVoiceConnecting(state.voiceChatSlice.connectionStatus)

export const selectVoiceOpenaiCallId = (state: VoiceRootState) =>
  state.voiceChatSlice.openaiCallId

export const selectAssistantWindowGated = (state: VoiceRootState) =>
  state.voiceChatSlice.assistantWindowGated

export const selectAssistantAudioStalled = (state: VoiceRootState) =>
  state.voiceChatSlice.assistantAudioStalled

export const selectVoiceMuted = (state: VoiceRootState) =>
  state.voiceChatSlice.muted

export const selectVoicePaused = (state: VoiceRootState) =>
  state.voiceChatSlice.paused

export const selectVoiceErrorMessage = (state: VoiceRootState) =>
  state.voiceChatSlice.errorMessage

export const selectVoiceLiveStream = (state: VoiceRootState) =>
  state.voiceChatSlice.liveStream

// The single live item, by id. Immer keeps every item's reference stable
// except the one a pacing tick mutates, so a per-row `useAppSelector` over
// this re-renders only the turn that's actually revealing.
export const selectVoiceLiveItem = (
  state: VoiceRootState,
  itemId: string
): VoiceLiveItem | undefined =>
  state.voiceChatSlice.liveStream.find(
    (entry): entry is Extract<VoiceLiveEntry, { kind: "item" }> =>
      entry.kind === "item" && entry.item.itemId === itemId
  )?.item

// Whether any assistant turn has generated transcript text. The shell mounts
// before the reveal begins so the pending-audio status can render without
// exposing that text. A boolean, so it recomputes on every reveal tick but only
// flips the subscriber's value once.
export const selectHasVoiceTranscript = createSelector(
  [selectVoiceLiveStream],
  (liveStream) =>
    liveStream.some(
      (entry) =>
        entry.kind === "item" &&
        entry.item.role === "assistant" &&
        entry.item.text.length > 0
    )
)

// The caption's CANDIDATE window: the most recent assistant turns with
// generated transcript text, in stream order, bounded only by the DOM ceiling.
// A candidate can remain text-empty in the DOM until pacing reveals its prefix.
// This is deliberately not the "what's on screen" decision — that's geometry,
// owned by the caption component, which retires a candidate once it has fully
// scrolled above the visible band. A character/turn budget here was a poor proxy for
// pixels (how many lines a turn occupies depends on viewport width, wrapping,
// and breakpoint), so the selector only bounds how much can ever mount.
//
// Deliberately NOT scoped to "since the user last spoke" — that boundary
// emptied the window the instant a user item landed (on audio-buffer commit),
// long before the assistant's reply streamed in, blanking the caption through
// the user's whole turn plus model/tool latency. Trailing instead keeps the
// previous answer on screen as the user responds; when the new answer starts
// revealing, the frontier auto-scroll centers it and the older lines scroll up
// and fade under the caption's top mask. Stable across reveal ticks (ids don't
// change as text fills), so the caption's map keeps referentially-stable rows
// and only the revealing row re-renders. Pair with `shallowEqual` at the call
// site.
export const selectRecentCaptionItemIds = createSelector(
  [selectVoiceLiveStream],
  (liveStream): string[] =>
    liveStream
      .flatMap((entry) =>
        entry.kind === "item" &&
        entry.item.role === "assistant" &&
        entry.item.text.length > 0
          ? [entry.item.itemId]
          : []
      )
      .slice(-MAX_CAPTION_TURNS)
)

// Keep pending calls visible across customer turns. Terminal calls remain
// visible after the latest customer item so their exit choreography can
// complete without retaining older completed calls.
export const selectVisibleVoiceToolCalls = createSelector(
  [selectVoiceLiveStream],
  (liveStream): VoiceToolCall[] => {
    const lastUserEntryIndex = liveStream.reduce(
      (last, entry, index) =>
        entry.kind === "item" && entry.item.role === "user" ? index : last,
      -1
    )
    return liveStream.flatMap((entry, index) =>
      entry.kind === "tool" &&
      !UI_HIDDEN_VOICE_TOOL_NAMES.has(entry.toolCall.toolName) &&
      (entry.toolCall.status === "pending" || index > lastUserEntryIndex)
        ? [entry.toolCall]
        : []
    )
  }
)

// The newest assistant turn with transcript text — the one turn whose cards
// are still arriving, so the only group that gets the animated cascade. A
// primitive, so it changes only at turn boundaries, never on reveal ticks.
export const selectLatestAssistantItemId = createSelector(
  [selectVoiceLiveStream],
  (liveStream): string | undefined => {
    for (let index = liveStream.length - 1; index >= 0; index--) {
      const entry = liveStream[index]
      if (
        entry.kind === "item" &&
        entry.item.role === "assistant" &&
        entry.item.text.length > 0
      ) {
        return entry.item.itemId
      }
    }
    return undefined
  }
)

// One group of the carousel strip: a card-bearing assistant turn and the cards
// it owns.
type CarouselStripTurn = {
  itemId: string
  places: PlaceSummary[]
  hotelQuoteRefs: string[]
}

// Every card-bearing assistant turn, oldest first — the strip's groups, left to
// right. A turn carries cards if it has a flight summary or a place mention
// that has been fully heard; reveal-gated rather than annotation-gated, so a
// turn joins the strip at the moment its first card can actually render.
//
// One card per place per SESSION, owned by the answer that first surfaced it: a
// follow-up that re-mentions a place points back at the card already on the
// strip instead of putting the same photo on it twice. First mention wins
// rather than last because the alternative deletes a card out of history — an
// answer the customer may be scrolled to and reading. The trade is that an
// answer whose every mention is a repeat carries no cards of its own and opens
// no group at all, so the light stays on the last answer that did.
//
// Rebuilt on every reveal tick, so subscribe through the shallowEqual wrappers
// below rather than to this.
const selectCarouselStripTurns = createSelector(
  [selectVoiceLiveStream],
  (liveStream): CarouselStripTurn[] => {
    const cardedRefs = new Set<string>()
    const cardedQuoteRefs = new Set<string>()
    // Hotel quote refs ride the tool call, which sits in the stream ahead of
    // the answer that presents them (a completed call is moved to the stream
    // tail, before the answer's items land) — so refs pend until the next
    // assistant item, the turn that owns their cards.
    let pendingQuoteRefs: string[] = []
    const turns: CarouselStripTurn[] = []
    for (const entry of liveStream) {
      if (entry.kind === "tool") {
        for (const ref of entry.toolCall.hotelQuoteRefs) {
          // Same once-per-session rule as places: every group's cards render
          // as siblings in one shared <ul>, so a repeated ref would collide
          // React keys across groups, not just show a duplicate card.
          if (cardedQuoteRefs.has(ref)) {
            continue
          }
          cardedQuoteRefs.add(ref)
          pendingQuoteRefs.push(ref)
        }
        continue
      }
      if (entry.kind !== "item" || entry.item.role !== "assistant") {
        continue
      }
      const hotelQuoteRefs = pendingQuoteRefs
      pendingQuoteRefs = []
      // A flight answer's mentions are dominated by its own route endpoints
      // (origin/destination cities and airports), so a flight turn cards no
      // place mentions at all — its group is the bookable flight options, and
      // the mentions stay bolded in the caption. Skipped BEFORE claiming, so
      // a later answer can still put those places on the strip.
      const isFlightTurn = entry.item.flightSummaryMessageId !== undefined
      const places: PlaceSummary[] = []
      if (!isFlightTurn) {
        for (const place of revealedItemPlaces(entry.item)) {
          if (cardedRefs.has(place.entity_ref)) {
            continue
          }
          cardedRefs.add(place.entity_ref)
          places.push(place)
        }
      }
      if (
        entry.item.flightSummaryMessageId === undefined &&
        places.length === 0 &&
        hotelQuoteRefs.length === 0
      ) {
        continue
      }
      turns.push({ itemId: entry.item.itemId, places, hotelQuoteRefs })
    }
    return turns
  }
)

// The strip's groups, left to right. Returns a fresh array per reveal tick;
// subscribe with shallowEqual.
export const selectCarouselStripTurnIds = createSelector(
  [selectCarouselStripTurns],
  (turns): string[] => turns.map((turn) => turn.itemId)
)

// The place cards one group renders — its own share of the session's places.
// Subscribe with shallowEqual: the array is rebuilt per reveal tick, but the
// PlaceSummary objects in it are immer-stable, so a settled group's
// subscription then holds still (and the admission queue, which compares item
// references, isn't re-synced forever).
export const selectCarouselTurnPlaces = (
  state: VoiceRootState,
  itemId: string
): PlaceSummary[] =>
  selectCarouselStripTurns(state).find((turn) => turn.itemId === itemId)
    ?.places ?? []

// The bookable hotel quote refs one group renders, in presentation order.
// Subscribe with shallowEqual, as with selectCarouselTurnPlaces — the refs
// are strings, so a settled group's subscription holds still.
export const selectCarouselTurnHotelQuoteRefs = (
  state: VoiceRootState,
  itemId: string
): string[] =>
  selectCarouselStripTurns(state).find((turn) => turn.itemId === itemId)
    ?.hotelQuoteRefs ?? []

// The audible response's assistant items in stream order. Memoized so the
// caption pacer's per-tick inputs (text, revealed length) aren't rebuilt on
// re-renders that don't touch the live stream — most importantly the orb's
// ~25Hz audio-level updates, which re-render the voice surface far more often
// than the caption changes.
const selectAudibleAssistantItems = createSelector(
  [selectVoiceLiveStream, selectAudibleAssistantResponseId],
  (liveStream, audibleAssistantResponseId) =>
    liveStream.flatMap((entry) =>
      entry.kind === "item" &&
      entry.item.role === "assistant" &&
      entry.item.responseId === audibleAssistantResponseId
        ? [entry.item]
        : []
    )
)

// The pacer's view of the audible window, NOT the full turns: a resumed
// pause continuation merges into a turn whose heard prefix was spoken in the
// window BEFORE the pause (pausePrefixLength), and this window's audio only
// speaks the text past it. Feeding the pacer the full text/reveal counted the
// prefix as this window's already-revealed speech, which ran its deficit
// negative by the prefix's whole speech time and froze the reveal.
export const selectAudibleAssistantText = createSelector(
  [selectAudibleAssistantItems],
  (items) =>
    items.map((item) => item.text.slice(item.pausePrefixLength ?? 0)).join("")
)

export const selectAudibleAssistantRevealedLength = createSelector(
  [selectAudibleAssistantItems],
  (items) =>
    items.reduce(
      (length, item) =>
        length +
        Math.max(0, item.revealedLength - (item.pausePrefixLength ?? 0)),
      0
    )
)

// ~8 seconds of speech: above any one-sentence tool narration, low enough
// that a real answer qualifies while barely under way.
const PAUSABLE_TURN_MIN_CHARS = 100

// Whether the pause control is live. Status alone flickered through a tool
// chain — enabled for each short narration, disabled for the tool wait,
// enabled again for the answer (MIN-13236) — so pause only arms once the
// audible turn has proven substantial. Total generated length is the signal
// because generation outruns playback (a long answer crosses the bar about
// when its audio starts) and it only grows within a turn — an
// unspoken-remainder test would drain back under the bar and kill the button
// seconds before every answer's end. While paused the same button is Resume,
// which always stays live.
export const selectVoicePausable = createSelector(
  [selectVoicePaused, selectVoiceConnectionStatus, selectAudibleAssistantItems],
  (paused, connectionStatus, audibleItems) => {
    if (paused) return true
    if (connectionStatus !== "assistant_speaking") return false
    const generatedLength = audibleItems.reduce(
      (length, item) => length + item.text.length,
      0
    )
    return generatedLength >= PAUSABLE_TURN_MIN_CHARS
  }
)

export const selectVoiceModeOpen = (state: VoiceRootState) =>
  state.voiceChatSlice.voiceModeOpen

export const selectVoiceModeChatId = (state: VoiceRootState) =>
  state.voiceChatSlice.voiceModeChatId

export const selectVoiceModeMinimized = (state: VoiceRootState) =>
  state.voiceChatSlice.voiceModeMinimized

export const selectVoicePipPosition = (state: VoiceRootState) =>
  state.voiceChatSlice.voicePipPosition

export const selectVoiceModeUserSpoke = (state: VoiceRootState) =>
  state.voiceChatSlice.voiceModeUserSpoke

// The voice-mode button's open flow, which starts at the click and can die
// before voiceModeOpened (mic refusal, chat creation). Nothing in the slice
// reacts: these exist so the middleware can bracket every attempt — including
// the ones that never become a session — with one analytics session.
export const voiceModeRequested = createAction<{ chatId: number | null }>(
  "voiceChat/modeRequested"
)
export const voiceModeOpenFailed = createAction<{
  failure: VoiceSessionFailure
  expected?: boolean
}>("voiceChat/modeOpenFailed")

export const voiceSessionStartRequested = createAction<{
  chatId: number
  mediaStream: Promise<MediaStream>
  audioProfile: VoiceAudioProfile
}>("voiceChat/sessionStartRequested")
export const voiceSessionStopRequested = createAction<StopVoiceSessionArgs>(
  "voiceChat/sessionStopRequested"
)
export const voiceAssistantStopRequested = createAction(
  "voiceChat/assistantStopRequested"
)
export const voiceChatActions = voiceChatSlice.actions

export default voiceChatSlice
